import { QueryLazyOptions } from '@apollo/client';
import React, { MutableRefObject } from 'react';
import { kodplaner_ekkodplaner } from '../../../../../../../Components/KodplanManager/Queries/_QueryTypes/kodplaner';
import {
  TEDEKBokforderRadLIKVIDSATTEnum,
  TEDEKBokforderVariantEnum,
  TEDOPOrgPersLokTypEnum,
} from '../../../../../../../globalQueryTypes';
import { FormUpdateAssociatonType } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import { IValues } from '../../../../../_StandardRutin/Mutations/ObjectMutations/ObjectUpdateMutationBase';
import { BokforderSettingsQuery_db_EKBokforingsmall_All } from '../../Queries/BokforderSettingsQuery/_QueryTypes/BokforderSettingsQuery';
import {
  KonteringsmallQuery,
  KonteringsmallQueryVariables,
} from '../BokforderCreateDialog/Queries/_QueryTypes/KonteringsmallQuery';
import {
  BfoRowsForTemplateType,
  DefaultBfoVariantEnum,
  IBfoUrlParams,
  ITemplateRow,
  IUserBokforderKodbehorigheter,
} from './BokforderContextManager';

export interface IBokforderContext {
  addOrUpdateMessage: (message: string, id: string, _version?: number) => void;
  allBokforderUsersWithKodbeh: IUserBokforderKodbehorigheter[];
  allUtanordningUsersWithKodbeh: IUserBokforderKodbehorigheter[];
  areYouSureModalCallback: {
    callback: (args?: any) => void;
  };
  areYouSureModalOpen: boolean;
  bokforderDate: Date;
  bokforderKodplan: kodplaner_ekkodplaner | undefined;
  bokforderVariant: TEDEKBokforderVariantEnum | DefaultBfoVariantEnum | null;
  currentTemplate: BokforderSettingsQuery_db_EKBokforingsmall_All | null;
  makeNewRow: (id: string, newValues?: IValues) => FormUpdateAssociatonType[];
  createTemplateRows: (newRows: ITemplateRow[]) => void;
  deleteBokforderRad: (radID: string, ignoreFormHasChanged?: boolean) => void;
  deleteMessage: (messageID: string) => void;
  editMessageID: string;
  everyReservAttesterIsAttesterade: boolean;
  focusRef: MutableRefObject<HTMLInputElement | undefined> | undefined;
  getKonteringsmall: (
    options?: QueryLazyOptions<KonteringsmallQueryVariables> | undefined
  ) => void;
  hasVerifikationFakturabild: boolean;
  hasVerifikationUnderlag: boolean;
  importDialogOpen: boolean;
  isAttestant: boolean;
  isFromEkA: boolean;
  isEditMode: boolean;
  isReservForAttestant: boolean;
  isReservForKontrollattestant: boolean;
  isVerifikation: boolean;
  konteringsmallarData: KonteringsmallQuery | undefined;
  konteringsmallarLoading: boolean;
  limitedCreateMode: boolean | null;
  makeTemplateRowsArray: (
    option: BokforderSettingsQuery_db_EKBokforingsmall_All | null,
    rowsFromBokforder?: BfoRowsForTemplateType[] | null
  ) => ITemplateRow[];
  methodOfLiquidation: TEDEKBokforderRadLIKVIDSATTEnum[];
  newRowIndex: number;
  onTemplateChange: (
    option:
      | BokforderSettingsQuery_db_EKBokforingsmall_All
      | BokforderSettingsQuery_db_EKBokforingsmall_All[]
      | null
  ) => void;
  paramValues: { [x: string]: string };
  resetBokforder: () => void;
  setAreYouSureModalCallback: React.Dispatch<
    React.SetStateAction<{
      callback: (args?: any) => void;
    }>
  >;
  setAreYouSureModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setBokforderVariant: React.Dispatch<
    React.SetStateAction<
      TEDEKBokforderVariantEnum | DefaultBfoVariantEnum | null
    >
  >;
  setEditMessageID: React.Dispatch<React.SetStateAction<string>>;
  setHasVerifikationFakturabild: React.Dispatch<React.SetStateAction<boolean>>;
  setHasVerifikationUnderlag: React.Dispatch<React.SetStateAction<boolean>>;
  setImportDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  setNewRowIndex: React.Dispatch<React.SetStateAction<number>>;
  setParamValues: React.Dispatch<React.SetStateAction<{ [x: string]: string }>>;
  setTillgohavandeKundTyp: React.Dispatch<
    React.SetStateAction<TEDOPOrgPersLokTypEnum | undefined>
  >;
  showAttestantKonteraSwitch: boolean;
  tillgodohavandeKundTyp: TEDOPOrgPersLokTypEnum | undefined;
  templates: BokforderSettingsQuery_db_EKBokforingsmall_All[];
  templateRows: ITemplateRow[];
  urlParams: IBfoUrlParams;
  updateRow: (id: string, newValues?: IValues) => FormUpdateAssociatonType[];
}

const defaults: IBokforderContext = {
  addOrUpdateMessage: () => {},
  allBokforderUsersWithKodbeh: [],
  allUtanordningUsersWithKodbeh: [],
  areYouSureModalCallback: { callback: (args?: any) => {} },
  areYouSureModalOpen: false,
  bokforderDate: new Date(),
  bokforderKodplan: undefined,
  bokforderVariant: TEDEKBokforderVariantEnum.TOM,
  currentTemplate: null,
  makeNewRow: () => [],
  createTemplateRows: () => {},
  deleteBokforderRad: () => {},
  deleteMessage: () => {},
  editMessageID: '',
  everyReservAttesterIsAttesterade: false,
  focusRef: undefined,
  getKonteringsmall: () => {},
  hasVerifikationFakturabild: false,
  hasVerifikationUnderlag: false,
  importDialogOpen: false,
  isAttestant: false,
  isFromEkA: false,
  isEditMode: false,
  isReservForAttestant: false,
  isReservForKontrollattestant: false,
  isVerifikation: false,
  konteringsmallarData: undefined,
  konteringsmallarLoading: false,
  limitedCreateMode: false,
  makeTemplateRowsArray: () => [],
  methodOfLiquidation: [],
  newRowIndex: 0,
  onTemplateChange: () => {},
  paramValues: {},
  resetBokforder: () => {},
  setAreYouSureModalCallback: () => {},
  setAreYouSureModalOpen: () => {},
  setBokforderVariant: () => {},
  setEditMessageID: () => {},
  setHasVerifikationFakturabild: () => {},
  setHasVerifikationUnderlag: () => {},
  setTillgohavandeKundTyp: () => {},
  setImportDialogOpen: () => {},
  setIsEditMode: () => {},
  setNewRowIndex: () => {},
  setParamValues: () => {},

  showAttestantKonteraSwitch: false,
  tillgodohavandeKundTyp: undefined,
  templates: [],
  templateRows: [],
  urlParams: {
    bokfar: null,
    vernr: null,
    variant: null,
  },
  updateRow: () => [],
};

const BokforderContext = React.createContext<IBokforderContext>(defaults);

export default BokforderContext;
